import React, { useEffect, useRef, useState } from 'react';

// Member photo on a bio page.
// "Change photo" only shows to the logged-in owner of this bio page (the server checks too).
// The photo is cropped to portrait 2:3 in the browser (drag to move, slider to zoom),
// then uploaded; the server saves it as assets/<slug>.png, replacing the old one.
// Until the server has the photo, the image bundled with the site (fallback) is shown.
// A member may change their photo once every 30 days; the server enforces it and
// tells us when the next change is allowed.

const FRAME_W = 240;
const FRAME_H = 360;
const OUT_W = 600;
const OUT_H = 900;

const showDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// the login pass, if the logged-in member owns this bio page
function ownerToken(slug) {
  try {
    const token = localStorage.getItem('bioToken');
    const bioUrl = (localStorage.getItem('bio_url') || '').replace(/\/+$/, '');
    return token && bioUrl === `/bio${slug}` ? token : null;
  } catch (err) {
    return null;
  }
}

function BioPhoto({ slug, fallback, className, alt }) {
  const [token] = useState(() => ownerToken(slug));
  const [src, setSrc] = useState(`/bio-photo/${slug}`);
  const [editing, setEditing] = useState(null); // { img, zoom, x, y }
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [nextChange, setNextChange] = useState(null); // date the photo may change again, if locked
  const fileInput = useRef(null);
  const canvas = useRef(null);
  const drag = useRef(null);

  // scale at which the image exactly covers the frame, times the zoom
  const scaleOf = (e) => Math.max(FRAME_W / e.img.width, FRAME_H / e.img.height) * e.zoom;

  // keep the image covering the whole frame
  const clamp = (e) => {
    const s = scaleOf(e);
    const minX = FRAME_W - e.img.width * s;
    const minY = FRAME_H - e.img.height * s;
    return { ...e, x: Math.min(0, Math.max(minX, e.x)), y: Math.min(0, Math.max(minY, e.y)) };
  };

  const draw = (ctx, e, k) => {
    const s = scaleOf(e);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, FRAME_W * k, FRAME_H * k);
    ctx.drawImage(e.img, e.x * k, e.y * k, e.img.width * s * k, e.img.height * s * k);
  };

  useEffect(() => {
    if (!token) return;
    fetch(`/api/bio-photo-status/${slug}`, { headers: { 'x-access-token': token } })
      .then(res => res.json())
      .then(data => { if (data.nextChange) setNextChange(data.nextChange); })
      .catch(() => {});
  }, [slug, token]);

  useEffect(() => {
    if (editing && canvas.current) {
      draw(canvas.current.getContext('2d'), editing, 1);
    }
  }, [editing]);

  const chooseFile = (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setStatus('Please choose an image file.');
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const start = { img, zoom: 1, x: 0, y: 0 };
      const s = scaleOf(start);
      setEditing(clamp({ ...start, x: (FRAME_W - img.width * s) / 2, y: (FRAME_H - img.height * s) / 2 }));
      setStatus('');
    };
    img.onerror = () => setStatus('This image could not be opened. Please try a JPG or PNG.');
    img.src = url;
  };

  const changeZoom = (event) => {
    const zoom = Number(event.target.value);
    setEditing((e) => {
      // zoom around the centre of the frame
      const ratio = zoom / e.zoom;
      const cx = FRAME_W / 2;
      const cy = FRAME_H / 2;
      return clamp({ ...e, zoom, x: cx - (cx - e.x) * ratio, y: cy - (cy - e.y) * ratio });
    });
  };

  const startDrag = (event) => {
    drag.current = { px: event.clientX, py: event.clientY, x: editing.x, y: editing.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveDrag = (event) => {
    if (!drag.current) return;
    const d = drag.current;
    setEditing((e) => clamp({ ...e, x: d.x + event.clientX - d.px, y: d.y + event.clientY - d.py }));
  };

  const endDrag = () => {
    drag.current = null;
  };

  const save = () => {
    const out = document.createElement('canvas');
    out.width = OUT_W;
    out.height = OUT_H;
    draw(out.getContext('2d'), editing, OUT_W / FRAME_W);

    setSaving(true);
    setStatus('Uploading...');

    out.toBlob(async (blob) => {
      try {
        const form = new FormData();
        form.append('photo', blob, `${slug}.jpg`);

        const res = await fetch(`/api/bio-photo/${slug}`, {
          method: 'POST',
          headers: { 'x-access-token': token },
          body: form
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          if (data.nextChange) {
            setNextChange(data.nextChange);
            setEditing(null);
          }
          throw new Error(data.message || 'Upload failed');
        }

        setSrc(data.url);
        setEditing(null);
        setNextChange(data.nextChange);
        setStatus('Photo updated!');
      } catch (err) {
        setStatus(err.message);
      } finally {
        setSaving(false);
      }
    }, 'image/jpeg', 0.92);
  };

  const warningStyle = {
    margin: '8px auto',
    padding: '8px 12px',
    maxWidth: '320px',
    border: '1px solid #d9534f',
    borderRadius: '6px',
    background: '#fdf2f2',
    color: '#a94442',
    fontSize: '0.9em',
  };

  const buttonStyle = {
    margin: '4px',
    padding: '6px 14px',
    borderRadius: '6px',
    border: '1px solid #888',
    background: '#fff',
    cursor: 'pointer',
  };

  return (
    <div>
      {!editing && (
        <img
          className={className}
          src={src}
          alt={alt}
          onError={() => { if (src !== fallback) setSrc(fallback); }}
        />
      )}

      {editing && (
        <div>
          <canvas
            ref={canvas}
            width={FRAME_W}
            height={FRAME_H}
            style={{ border: '2px solid #333', cursor: 'move', touchAction: 'none', maxWidth: '100%' }}
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />
          <div style={{ fontSize: '0.9em' }}>Drag to move, slide to zoom</div>
          <input
            type="range"
            min="1"
            max="3"
            step="0.01"
            value={editing.zoom}
            onChange={changeZoom}
            style={{ width: FRAME_W, maxWidth: '100%' }}
          />
          <div style={warningStyle}>
            <strong>Warning:</strong> you can change your photo only once every 30 days.
            After you save, you cannot change it again until {showDate(Date.now() + 30 * 24 * 60 * 60 * 1000)}.
            To change it sooner, please contact the admin.
          </div>
          <div>
            <button type="button" style={buttonStyle} onClick={save} disabled={saving}>Save photo</button>
            <button type="button" style={buttonStyle} onClick={() => { setEditing(null); setStatus(''); }} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {token && !editing && !nextChange && (
        <div>
          <button type="button" style={buttonStyle} onClick={() => fileInput.current.click()}>
            Change photo
          </button>
          <div style={{ fontSize: '0.85em', color: '#a94442' }}>You can change your photo only once every 30 days.</div>
        </div>
      )}

      {token && !editing && nextChange && (
        <div style={warningStyle}>
          You can change your photo again on {showDate(nextChange)}.
          To change it sooner, please contact the admin.
        </div>
      )}

      {token && !nextChange && (
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={chooseFile}
        />
      )}

      {status && <div>{status}</div>}
    </div>
  );
}

export default BioPhoto;
